/**
 * UniformArrayNodePatch — mirrors an UNRELEASED upstream three.js fix
 * (dev branch, post-r185.1) for a crash that blanks every lit material in
 * WebXR.
 *
 * r185 `UniformArrayNode.setup()` seeds its GPU buffer via an ARGLESS
 * `this.update()`. `Node.onUpdate()` REPLACES `update` with the user callback,
 * and three's own XR accessor `cameraPositions` (Camera.js — the only camera
 * uniform-array WITH an onRenderUpdate callback) destructures `{ camera }`
 * from that missing frame:
 *
 *   TypeError: Cannot destructure property 'camera' of 'undefined'
 *
 * The build of EVERY material referencing `cameraPosition` under an
 * ArrayCamera (i.e. every lit material, in any immersive session) throws at
 * setup, and NodeManager's catch swaps the material for a BARE NodeMaterial —
 * the whole lit world silently vanishes in the headset (Quest 3 field beacon,
 * 2026-08-15).
 *
 * A second half of the same upstream fix: the r185 callback wrapper never
 * re-packs `.array` into the padded `.value` buffer after the callback runs,
 * so `cameraPositions` froze at its setup-time seed even when the build
 * survived. Upstream dev chains `updateBuffer()` after the callback; we chain
 * the default packer (`UniformArrayNode.prototype.update`), which is the same
 * routine.
 *
 * Both patches are idempotent, prototype-level, and inert for uniform arrays
 * without callbacks (the projection/view matrix arrays hold live references
 * and have no onUpdate). Remove when three ships the upstream fix
 * (UniformArrayNode.setup() calling `updateBuffer()` instead of `update()`).
 */

import * as THREE from 'three/webgpu';
import { Logger } from '../../core/Logger.js';

let applied = false;

export function applyUniformArrayNodePatch(): void {
  if (applied) return;
  applied = true;

  type Proto = {
    setup(builder: unknown): unknown;
    update(frame?: unknown): void;
    onUpdate(cb: (...a: unknown[]) => unknown, type: string): unknown;
    __xrUniformArrayPatch?: boolean;
  };
  const cls = (THREE as unknown as { UniformArrayNode?: { prototype: Proto } }).UniformArrayNode;
  const proto = cls?.prototype;
  // Mocked three (unit tests) or a future release without the class: no-op.
  if (!proto || typeof proto.setup !== 'function' || typeof proto.update !== 'function') return;
  if (proto.__xrUniformArrayPatch) return;
  proto.__xrUniformArrayPatch = true;

  const defaultUpdate = proto.update; // the array→padded-value packer
  const origSetup = proto.setup;
  proto.setup = function (this: Proto & Record<string, unknown>, builder: unknown): unknown {
    // Seed the buffer with the DEFAULT packer, never the user callback — the
    // callback needs a NodeFrame that does not exist at setup time.
    const ownUpdate = Object.prototype.hasOwnProperty.call(this, 'update');
    const cb = this.update;
    this.update = defaultUpdate;
    try {
      return origSetup.call(this, builder);
    } finally {
      if (ownUpdate) this.update = cb;
      else delete (this as Record<string, unknown>).update;
    }
  };

  const origOnUpdate = proto.onUpdate;
  proto.onUpdate = function (
    this: Proto,
    cb: (...a: unknown[]) => unknown,
    type: string,
  ): unknown {
    return origOnUpdate.call(
      this,
      function (this: Proto, ...a: unknown[]) {
        const v = cb.apply(this, a);
        defaultUpdate.call(this); // re-pack .array → .value after the callback
        return v;
      },
      type,
    );
  };

  Logger.info('XR', 'UniformArrayNode r185 XR crash patch applied (remove after three > 0.185.1)');
}

let foveationApplied = false;

/**
 * r185 second XR frame-loop crash: `onAnimationFrame` calls
 * `xr.foveateBoundTexture(renderer._getFrameBufferTarget())`, and
 * `_getFrameBufferTarget()` legitimately returns NULL whenever no output
 * post-process target is needed — `foveateBoundTexture(null)` then reads
 * `.isPostProcessingRenderTarget` off null and THROWS EVERY XR FRAME,
 * aborting the rest of onAnimationFrame: three's controller updates and the
 * app's animation loop never run inside the session. On-device this presents
 * as stale, head-lagged, misplaced eye layers (Quest 3 field report
 * 2026-08-15) — reproduced under the vr-sim emulated Quest 3. Guard the
 * entry. Remove after three > 0.185.1.
 */
export function applyFoveationNullGuardPatch(xrInstance: unknown): void {
  if (foveationApplied) return;
  type XrProto = { foveateBoundTexture?(rt: unknown): void; __xrFoveationGuard?: boolean };
  // XRManager is not re-exported from three/webgpu — reach its prototype via
  // the live renderer.xr instance.
  const proto = xrInstance ? (Object.getPrototypeOf(xrInstance) as XrProto) : null;
  if (!proto || typeof proto.foveateBoundTexture !== 'function') return;
  if (proto.__xrFoveationGuard) return;
  foveationApplied = true;
  proto.__xrFoveationGuard = true;
  const orig = proto.foveateBoundTexture;
  proto.foveateBoundTexture = function (rt: unknown): void {
    if (rt === null || rt === undefined) return;
    return orig.call(this, rt);
  };
  Logger.info('XR', 'foveateBoundTexture null-guard patch applied (remove after three > 0.185.1)');
}

let drawBuffersApplied = false;

/**
 * r185 third XR crash (WebGL backend): `WebGLState.drawBuffers` caches per-
 * framebuffer draw-buffer arrays in a WeakMap keyed by the framebuffer — but
 * an XR base layer's `framebuffer` is legally NULL (the default framebuffer:
 * inline sessions, emulators like IWER) and `WeakMap.set(null)` throws in
 * `finishRender` every frame, killing the whole tick. Real headsets hand out
 * opaque non-null framebuffers, so this bites emulated/inline sessions. The
 * default framebuffer's only valid draw-buffer set is [BACK] — emit exactly
 * that and skip the cache. Remove after three > 0.185.1.
 */
export function applyDrawBuffersNullGuardPatch(backend: unknown): void {
  if (drawBuffersApplied) return;
  type GlState = {
    gl: { BACK: number; drawBuffers(b: number[]): void };
    drawBuffers(rc: { textures: unknown }, fb: unknown): void;
    __xrDrawBuffersGuard?: boolean;
  };
  const state = (backend as { state?: GlState } | null)?.state;
  if (!state || typeof state.drawBuffers !== 'function') return;
  if (state.__xrDrawBuffersGuard) return;
  drawBuffersApplied = true;
  state.__xrDrawBuffersGuard = true;
  const orig = state.drawBuffers.bind(state);
  state.drawBuffers = (rc, fb) => {
    if (fb === null && rc.textures !== null) {
      state.gl.drawBuffers([state.gl.BACK]);
      return;
    }
    orig(rc, fb);
  };
  Logger.info('XR', 'WebGLState.drawBuffers null-framebuffer guard applied (remove after three > 0.185.1)');
}

/**
 * r185 fourth XR bug — THE eye-misalignment root cause (Quest 3 field saga:
 * overlapping eyes, upper-left offset, black band across the bottom).
 *
 * CanvasTarget.setSize/setDrawingBufferSize guard on `this.xr.isPresenting`,
 * but NOTHING in r185 ever assigns `canvasTarget.xr` — the guard is dead
 * code. Worse, CanvasTarget.setPixelRatio mutates `_pixelRatio` BEFORE its
 * (dead-)guarded setSize. The Quest browser reports devicePixelRatio 0.75,
 * so any quality pipeline calling `setPixelRatio(min(dpr, x))` mid-session
 * shrank the canvas bookkeeping to 75%: the render context viewport became
 * 0.75× the XR framebuffer and the per-eye viewports were multiplied by
 * 0.75 in the draw loop — content in the lower-left 3/4 of the layer, the
 * right eye bleeding into the left half. Reproduced under vr-sim at
 * --dpr 0.75.
 *
 * Fix both halves: wire the guard three intended (canvasTarget.xr) AND make
 * the renderer's setPixelRatio itself inert while presenting (three's own
 * session-end restore runs after isPresenting flips false, so it passes).
 * Remove after three > 0.185.1.
 */
export function applyPixelRatioXRGuardPatch(renderer: unknown): void {
  const r = renderer as {
    xr?: { isPresenting?: boolean };
    _canvasTarget?: { xr?: unknown };
    setPixelRatio?: (v?: number) => void;
    __xrPixelRatioGuard?: boolean;
  };
  if (!r || typeof r.setPixelRatio !== 'function' || r.__xrPixelRatioGuard) return;
  r.__xrPixelRatioGuard = true;
  if (r._canvasTarget && !r._canvasTarget.xr) r._canvasTarget.xr = r.xr;
  const orig = r.setPixelRatio.bind(r);
  r.setPixelRatio = (v?: number): void => {
    if (r.xr?.isPresenting) return;
    orig(v);
  };
  Logger.info('XR', 'setPixelRatio XR guard applied (remove after three > 0.185.1)');
}
